import { useEffect, useMemo, useState } from "react";
import { Copy, Download, ExternalLink, Eye, Power } from "lucide-react";
import { toast } from "sonner";
import AdminLayout from "@/components/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import Wordmark from "@/components/Wordmark";
import waveLogo from "@/assets/jersey-shore-wave-logo.png";
import { supabase } from "@/integrations/supabase/client";
import { downloadDataUrl, downloadSvg, makeQrPng, makeQrSvg, qrPrintUrl, qrTestUrl, shortCode, shortQrUrl, QR_DESTINATIONS, QR_PLACEMENTS, type QrDestination, type QrPlacement } from "@/lib/campaignQr";

type Athlete = { id: string; name: string; slug: string; image_path: string | null };
type QrRow = { id: string; code: string; destination_type: string; placement: string; athlete_id: string | null; active: boolean; created_at: string; deactivated_at: string | null; label: string | null };

type StatusTab = "active" | "inactive" | "all";
type SortKey = "newest" | "oldest" | "destination" | "placement";

const AdminQr = () => {
  const [athletes, setAthletes] = useState<Athlete[]>([]);
  const [codes, setCodes] = useState<QrRow[]>([]);
  const [destination, setDestination] = useState<QrDestination>("campaign");
  const [placement, setPlacement] = useState<QrPlacement>("stadium");
  const [athleteId, setAthleteId] = useState("");
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState<{ row: QrRow; svg: string; png: string } | null>(null);
  const [statusTab, setStatusTab] = useState<StatusTab>("active");
  const [destinationFilter, setDestinationFilter] = useState<"all" | QrDestination>("all");
  const [placementFilter, setPlacementFilter] = useState<"all" | QrPlacement>("all");
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("newest");

  const load = async () => {
    const [{ data: team }, { data: qrData, error }] = await Promise.all([
      supabase.from("teams").select("id").eq("slug", "jersy-shore-wave").eq("published", true).maybeSingle(),
      supabase.from("campaign_qr_codes").select("id,code,destination_type,placement,athlete_id,active,created_at,deactivated_at,label").order("created_at", { ascending: false }),
    ]);
    if (error) toast.error("QR codes could not be loaded.");
    setCodes((qrData as QrRow[]) ?? []);
    if (!team) return;
    const { data: links } = await supabase.from("athlete_teams").select("athlete_id").eq("team_id", team.id);
    const ids = (links ?? []).map((row) => row.athlete_id);
    if (!ids.length) return;
    const { data } = await supabase.from("athletes").select("id,name,slug,image_path").in("id", ids).eq("published", true).order("name");
    const rows = (data as Athlete[]) ?? [];
    setAthletes(rows);
    setAthleteId((current) => current || rows[0]?.id || "");
  };

  useEffect(() => { void load(); }, []);

  const selectedAthlete = useMemo(() => athletes.find((athlete) => athlete.id === athleteId), [athleteId, athletes]);

  const showPreview = async (row: QrRow) => {
    const url = qrPrintUrl(row.destination_type as QrDestination, row.code);
    const [svg, png] = await Promise.all([makeQrSvg(url), makeQrPng(url)]);
    setPreview({ row, svg, png });
  };

  const generate = async () => {
    if (destination === "athlete" && !athleteId) return;
    setBusy(true);
    const { data, error } = await supabase.rpc("create_wave_qr_code", {
      p_destination_type: destination,
      p_placement: placement,
      p_athlete_id: destination === "athlete" ? athleteId : null,
      p_label: label.trim() ? label.trim().slice(0, 80) : null,
    });
    setBusy(false);
    if (error || !data) return toast.error(error?.message ?? "QR code could not be generated.");
    const row = data as QrRow;
    setCodes((current) => [row, ...current]);
    setLabel("");
    setStatusTab("active");
    await showPreview(row);
    toast.success("QR code generated");
  };

  const deactivate = async (row: QrRow) => {
    const now = new Date().toISOString();
    const { error } = await supabase.from("campaign_qr_codes").update({ active: false, deactivated_at: now, updated_at: now }).eq("id", row.id).eq("active", true);
    if (error) return toast.error("QR code could not be deactivated.");
    setCodes((current) => current.map((item) => item.id === row.id ? { ...item, active: false, deactivated_at: now } : item));
    setPreview((current) => current?.row.id === row.id ? { ...current, row: { ...current.row, active: false, deactivated_at: now } } : current);
    toast.success("QR code deactivated");
  };

  const athleteName = (id: string | null) => athletes.find((athlete) => athlete.id === id)?.name;
  const fileStem = preview ? `wave-${preview.row.destination_type}-${preview.row.placement}-${preview.row.code}` : "wave-qr";

  const destinationLabel = (row: QrRow) =>
    row.destination_type === "athlete" ? athleteName(row.athlete_id) ?? "Wave athlete" : row.destination_type;

  const counts = useMemo(() => ({
    active: codes.filter((row) => row.active).length,
    inactive: codes.filter((row) => !row.active).length,
    all: codes.length,
  }), [codes]);

  const visibleCodes = useMemo(() => {
    const term = search.trim().toLowerCase();
    const rows = codes.filter((row) => {
      if (statusTab === "active" && !row.active) return false;
      if (statusTab === "inactive" && row.active) return false;
      if (destinationFilter !== "all" && row.destination_type !== destinationFilter) return false;
      if (placementFilter !== "all" && row.placement !== placementFilter) return false;
      if (!term) return true;
      const haystack = [destinationLabel(row), row.destination_type, row.placement, row.label ?? "", shortCode(row.code)].join(" ").toLowerCase();
      return haystack.includes(term);
    });
    const sorted = [...rows];
    sorted.sort((a, b) => {
      if (sortKey === "oldest") return a.created_at.localeCompare(b.created_at);
      if (sortKey === "destination") return destinationLabel(a).localeCompare(destinationLabel(b)) || b.created_at.localeCompare(a.created_at);
      if (sortKey === "placement") return a.placement.localeCompare(b.placement) || b.created_at.localeCompare(a.created_at);
      return b.created_at.localeCompare(a.created_at);
    });
    return sorted;
  }, [codes, search, statusTab, destinationFilter, placementFilter, sortKey, athletes]);

  const copyLink = async (row: QrRow) => {
    const url = qrPrintUrl(row.destination_type as QrDestination, row.code);
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied");
    } catch {
      toast.error("Link could not be copied.");
    }
  };

  const statusTabs: Array<{ key: StatusTab; label: string }> = [
    { key: "active", label: `Active (${counts.active})` },
    { key: "inactive", label: `Inactive (${counts.inactive})` },
    { key: "all", label: `All (${counts.all})` },
  ];

  return (
    <AdminLayout>
      <div className="space-y-8">
        <header><h1 className="font-display text-3xl">Wave QR Manager</h1><p className="mt-1 text-sm text-muted-foreground">Create stable, trackable campaign links for printed and digital placements.</p></header>
        <section className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
          <div className="space-y-5 rounded-md border border-border bg-card p-5">
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="text-sm font-semibold">Destination<select aria-label="Destination" value={destination} onChange={(e) => setDestination(e.target.value as QrDestination)} className="mt-1 h-11 w-full rounded-md border border-input bg-background px-3 font-normal">{QR_DESTINATIONS.map((value) => <option key={value} value={value}>{value[0].toUpperCase() + value.slice(1)}</option>)}</select></label>
              <label className="text-sm font-semibold">Placement<select aria-label="Placement" value={placement} onChange={(e) => setPlacement(e.target.value as QrPlacement)} className="mt-1 h-11 w-full rounded-md border border-input bg-background px-3 font-normal">{QR_PLACEMENTS.map((value) => <option key={value} value={value}>{value[0].toUpperCase() + value.slice(1)}</option>)}</select></label>
            </div>
            {destination === "athlete" && <label className="block text-sm font-semibold">Wave athlete<select aria-label="Wave athlete" value={athleteId} onChange={(e) => setAthleteId(e.target.value)} className="mt-1 h-11 w-full rounded-md border border-input bg-background px-3 font-normal">{athletes.map((athlete) => <option key={athlete.id} value={athlete.id}>{athlete.name}</option>)}</select></label>}
            <label className="block text-sm font-semibold">Label <span className="font-normal text-muted-foreground">(optional, admin only)</span><input aria-label="Label" value={label} maxLength={80} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Home opener program" className="mt-1 h-11 w-full rounded-md border border-input bg-background px-3 text-sm font-normal" /></label>
            <p className="text-sm text-muted-foreground">Destination preview: {destination === "campaign" ? "/wave" : destination === "roster" ? "/wave#roster" : selectedAthlete ? `/athlete/${selectedAthlete.slug}` : "Select an athlete"}</p>
            <Button onClick={generate} disabled={busy || (destination === "athlete" && !athleteId)}>{busy ? "Generating…" : "Generate QR code"}</Button>
          </div>

          <aside className="rounded-md border border-border bg-card p-5">
            {preview ? (
              <div className="mx-auto flex w-full max-w-[304px] flex-col items-center">
                <div className="w-full rounded-md bg-background p-4 shadow-card">
                  <div className="flex flex-wrap items-center justify-center gap-2 rounded-md bg-background p-3">
                    <Wordmark size="sm" />
                    <span aria-hidden>×</span>
                    <img src={waveLogo} alt="Jersey Shore Wave" className="h-9 w-auto" />
                  </div>
                  <div className="mx-auto w-full max-w-[220px] rounded-md bg-white p-3 shadow-sm sm:max-w-[280px] [&_svg]:h-auto [&_svg]:w-full" dangerouslySetInnerHTML={{ __html: preview.svg }} />
                </div>
                <div className="mt-4 w-full text-center">
                  <p className="font-display text-xl">Know her name. Back her game.</p>
                  <p className="break-all text-xs text-muted-foreground">{qrPrintUrl(preview.row.destination_type as QrDestination, preview.row.code)}</p>
                  <a className="mt-1 inline-block break-all text-xs text-primary underline" href={qrTestUrl(preview.row.destination_type as QrDestination, preview.row.code)} target="_blank" rel="noreferrer">Test this link here</a>
                  {!preview.row.active && <p className="text-sm font-semibold text-destructive">Inactive</p>}
                </div>
                <div className="mt-4 grid w-full max-w-[220px] grid-cols-2 gap-2 sm:max-w-[280px]">
                  <Button variant="outline" onClick={() => downloadSvg(preview.svg, `${fileStem}.svg`)}><Download className="h-4 w-4" /> SVG</Button>
                  <Button variant="outline" onClick={() => downloadDataUrl(preview.png, `${fileStem}.png`)}><Download className="h-4 w-4" /> PNG</Button>
                </div>
              </div>
            ) : <p className="text-sm text-muted-foreground">Generate or preview a code to see the branded print presentation.</p>}
          </aside>
        </section>

        <section className="min-w-0 space-y-4">
          <h2 className="font-display text-2xl">Generated codes</h2>

          <div role="tablist" aria-label="Code status" className="flex flex-wrap gap-2">
            {statusTabs.map((tab) => (
              <button
                key={tab.key}
                role="tab"
                type="button"
                aria-selected={statusTab === tab.key}
                onClick={() => setStatusTab(tab.key)}
                className={`h-10 rounded-md border px-3 text-sm font-semibold ${statusTab === tab.key ? "border-primary bg-primary text-primary-foreground" : "border-input bg-background text-foreground"}`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <label className="text-sm font-semibold">Destination<select aria-label="Filter by destination" value={destinationFilter} onChange={(e) => setDestinationFilter(e.target.value as "all" | QrDestination)} className="mt-1 h-11 w-full rounded-md border border-input bg-background px-3 font-normal"><option value="all">All</option>{QR_DESTINATIONS.map((value) => <option key={value} value={value}>{value[0].toUpperCase() + value.slice(1)}</option>)}</select></label>
            <label className="text-sm font-semibold">Placement<select aria-label="Filter by placement" value={placementFilter} onChange={(e) => setPlacementFilter(e.target.value as "all" | QrPlacement)} className="mt-1 h-11 w-full rounded-md border border-input bg-background px-3 font-normal"><option value="all">All</option>{QR_PLACEMENTS.map((value) => <option key={value} value={value}>{value[0].toUpperCase() + value.slice(1)}</option>)}</select></label>
            <label className="text-sm font-semibold">Search<input aria-label="Search codes" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Destination, placement, label, code" className="mt-1 h-11 w-full rounded-md border border-input bg-background px-3 text-sm font-normal" /></label>
            <label className="text-sm font-semibold">Sort<select aria-label="Sort codes" value={sortKey} onChange={(e) => setSortKey(e.target.value as SortKey)} className="mt-1 h-11 w-full rounded-md border border-input bg-background px-3 font-normal"><option value="newest">Newest</option><option value="oldest">Oldest</option><option value="destination">Destination A–Z</option><option value="placement">Placement</option></select></label>
          </div>

          <p className="text-sm text-muted-foreground">Showing {visibleCodes.length} of {counts.all} codes. Inactive codes are kept for traceability.</p>

          <div className="max-w-full overflow-x-auto rounded-md border border-border">
            <table className="w-full min-w-[760px] text-left text-sm">
              <thead className="bg-muted"><tr><th className="p-3">Destination</th><th className="p-3">Placement</th><th className="p-3">Label</th><th className="p-3">Link</th><th className="p-3">Status</th><th className="p-3">Actions</th></tr></thead>
              <tbody>
                {visibleCodes.map((row) => (
                  <tr key={row.id} className="border-t border-border">
                    <td className="p-3 font-semibold capitalize">{destinationLabel(row)}</td>
                    <td className="p-3 capitalize">{row.placement}</td>
                    <td className="p-3 text-muted-foreground">{row.label ?? "—"}</td>
                    <td className="p-3">
                      <span className="font-mono text-xs">{shortQrUrl(row.destination_type as QrDestination, row.code)}</span>
                      <span className="mt-1 flex gap-1">
                        <Button size="sm" variant="outline" aria-label={`Copy link for ${shortCode(row.code)}`} onClick={() => copyLink(row)}><Copy className="h-3.5 w-3.5" /> Copy</Button>
                        <Button size="sm" variant="outline" asChild><a href={qrTestUrl(row.destination_type as QrDestination, row.code)} target="_blank" rel="noreferrer" aria-label={`Open link for ${shortCode(row.code)}`}><ExternalLink className="h-3.5 w-3.5" /> Open</a></Button>
                      </span>
                    </td>
                    <td className="p-3">{row.active ? "Active" : "Inactive"}</td>
                    <td className="p-3"><div className="flex gap-2"><Button size="icon" variant="outline" aria-label={`Preview ${shortCode(row.code)}`} title="Preview" onClick={() => showPreview(row)}><Eye /></Button>{row.active && <Button size="icon" variant="outline" aria-label={`Deactivate ${shortCode(row.code)}`} title="Deactivate" onClick={() => deactivate(row)}><Power /></Button>}</div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {visibleCodes.length === 0 && <p className="text-sm text-muted-foreground">{counts.all === 0 ? "No campaign QR codes yet." : "No codes match these filters."}</p>}
        </section>
      </div>
    </AdminLayout>
  );
};

export default AdminQr;
